import type { ReactNode } from "react";
import { useClientTranslations } from "../../../../lib/i18n/client";
import styles from "./FormField.module.css";

interface FormFieldProps {
	/** Used for label htmlFor + aria-describedby on the error message. The child input must use the same id. */
	id: string;
	label: string;
	required?: boolean;
	error?: string;
	children: ReactNode;
}

/**
 * Phase 37 — Field wrapper primitive: label + required asterisk + children + error.
 *
 * Composition primitive used by every field in the 4 forms. Children are responsible
 * for setting `id={fieldId}` and `aria-describedby={`${fieldId}-error`}` on their
 * root focusable element so the label/error semantics connect.
 */
export function FormField({
	id,
	label,
	required,
	error,
	children,
}: FormFieldProps) {
	const { t } = useClientTranslations("track");
	return (
		<div className={styles.field}>
			<label htmlFor={id} className={styles.label}>
				{label}
				{required ? (
					<span
						role="img"
						className={styles.required}
						aria-label={t("event.add.fields.required")}
					>
						*
					</span>
				) : null}
			</label>
			{children}
			{error ? (
				<p id={`${id}-error`} className={styles.error} role="alert">
					{error}
				</p>
			) : null}
		</div>
	);
}
